import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Target, Users, TrendingUp, Clock, CheckCircle, XCircle, Mail, Building2, Plus, Link as LinkIcon, Phone, Pencil, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  DRAFT: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Draft' },
  PROSPECT: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Prospect' },
  LINK_SENT: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Link Sent' },
  SUBMITTED: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Submitted' },
  NEEDS_INFO: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Needs Info' },
  APPROVED: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Approved' },
  DECLINED: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Declined' },
};

export default function SalesDashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isManager = user && ['master_admin', 'admin', 'sales_admin'].includes(user.role);
  const [view, setView] = useState('mine');   // 'mine' | 'team'
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);   // null = add mode, intake_id = edit mode
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '',
    num_trucks: '', credit_amount_requested: '', lead_source: '', notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [token, view]);

  const fetchData = async () => {
    try {
      const params = `?view=${view}`;
      const reqs = [
        fetch(`${API}/api/sales/stats${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/sales/my-leads${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/sales/my-customers${params}`, { headers: { Authorization: `Bearer ${token}` } }),
      ];
      if (isManager && view === 'team') {
        reqs.push(fetch(`${API}/api/sales/team-leaderboard`, { headers: { Authorization: `Bearer ${token}` } }));
        reqs.push(fetch(`${API}/api/sales/all-sales-reps`, { headers: { Authorization: `Bearer ${token}` } }));
      }
      const [statsRes, leadsRes, customersRes, lbRes, repsRes] = await Promise.all(reqs);

      if (statsRes.ok) setStats(await statsRes.json());
      if (leadsRes.ok) {
        const data = await leadsRes.json();
        setLeads(data.items || []);
      }
      if (customersRes.ok) {
        const data = await customersRes.json();
        setCustomers(data.items || []);
      }
      if (lbRes && lbRes.ok) {
        const data = await lbRes.json();
        setLeaderboard(data.leaderboard || []);
      } else {
        setLeaderboard([]);
      }
      if (repsRes && repsRes.ok) {
        const data = await repsRes.json();
        setSalesReps(data.sales_reps || []);
      }
    } catch (err) {
      console.error('Failed to fetch sales data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async (intakeId, repId) => {
    if (!repId) return;
    try {
      const res = await fetch(`${API}/api/sales/assign-lead/${intakeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sales_rep_id: repId }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Reassign failed');
      }
      toast.success('Lead reassigned');
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Reassign failed');
    }
  };

  const handleAddLead = async () => {
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setAdding(true);
    try {
      const url = editingId
        ? `${API}/api/sales/lead/${editingId}`
        : `${API}/api/sales/quick-add-lead`;
      const method = editingId ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        // FastAPI returns Pydantic validation errors as an array under `detail`;
        // simple HTTPExceptions return a string. Surface both clearly.
        let msg = 'Save failed';
        if (typeof e.detail === 'string') msg = e.detail;
        else if (Array.isArray(e.detail) && e.detail.length) {
          msg = e.detail.map(x => {
            const field = Array.isArray(x.loc) ? x.loc.slice(-1)[0] : '';
            return field ? `${field}: ${x.msg}` : x.msg;
          }).join(' · ');
        }
        throw new Error(msg);
      }
      toast.success(editingId
        ? `Lead "${form.company_name}" updated`
        : `Lead "${form.company_name}" added to your pipeline`);
      closeModal();
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Could not save lead');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (lead) => {
    const biz = lead.form_data?.business_info || {};
    setForm({
      company_name: lead.company_name || biz.legal_business_name || '',
      contact_name: biz.person_responsible_for_bills || '',
      email: lead.contact_email || biz.email || '',
      phone: biz.phone || '',
      num_trucks: lead.num_trucks || biz.num_trucks || '',
      credit_amount_requested: lead.credit_requested || biz.credit_amount_requested || '',
      lead_source: lead.lead_source || '',
      notes: '',
    });
    setEditingId(lead.id);
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingId(null);
    setForm({ company_name: '', contact_name: '', email: '', phone: '',
      num_trucks: '', credit_amount_requested: '', lead_source: '', notes: '' });
  };

  const handleSendLink = async (intakeId, companyName, sendEmail = false) => {
    try {
      const res = await fetch(`${API}/api/sales/generate-intake-link/${intakeId}?send_email=${sendEmail}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'failed');
      }
      const data = await res.json();
      if (sendEmail) {
        if (data.emailed_to) {
          toast.success(`Application link emailed to ${data.emailed_to}`);
        } else if (data.email_failed) {
          toast.warning('Email failed — link copied to clipboard instead');
          try { await navigator.clipboard.writeText(data.url); } catch {}
        }
      } else {
        try {
          await navigator.clipboard.writeText(data.url);
          toast.success(`Intake link copied for ${companyName} — paste in LinkedIn/SMS/email`);
        } catch {
          toast.success(`Intake link ready: ${data.url}`);
        }
      }
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Could not generate link');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {view === 'team' ? 'Team Sales' : 'My Sales'}
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {view === 'team' ? 'All leads and customers across every rep' : 'Your leads and customer assignments'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Manager-only: View toggle */}
          {isManager && (
            <div className="flex rounded-md overflow-hidden" style={{ border: '1px solid var(--border)' }}>
              <button
                onClick={() => setView('mine')}
                className="px-3 py-1.5 text-sm transition-colors"
                style={{
                  background: view === 'mine' ? 'var(--primary)' : 'transparent',
                  color: view === 'mine' ? 'white' : 'var(--text-muted)',
                }}
                data-testid="view-mine"
              >
                My View
              </button>
              <button
                onClick={() => setView('team')}
                className="px-3 py-1.5 text-sm transition-colors"
                style={{
                  background: view === 'team' ? 'var(--primary)' : 'transparent',
                  color: view === 'team' ? 'white' : 'var(--text-muted)',
                }}
                data-testid="view-team"
              >
                All Team
              </button>
            </div>
          )}
          <Button
            onClick={() => setShowAddModal(true)}
            data-testid="add-lead-btn"
            className="gap-2"
          >
            <Plus size={16} />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Manager-only: Team Leaderboard (visible in team view) */}
      {isManager && view === 'team' && leaderboard.length > 0 && (
        <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <TrendingUp size={16} className="text-emerald-400" />
              Team Leaderboard — 30 day performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--text-muted)' }}>Rank</th>
                    <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--text-muted)' }}>Rep</th>
                    <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-muted)' }}>Total Leads</th>
                    <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-muted)' }}>30d New</th>
                    <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-muted)' }}>30d Wins</th>
                    <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-muted)' }}>Customers</th>
                    <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-muted)' }}>Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((rep, idx) => (
                    <tr key={rep.rep_id} style={{ borderBottom: '1px solid var(--border)' }} data-testid={`leaderboard-${rep.rep_id}`}>
                      <td className="py-2 px-2 font-mono" style={{ color: 'var(--text-muted)' }}>
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                      </td>
                      <td className="py-2 px-2">
                        <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{rep.rep_name}</div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{rep.rep_role}</div>
                      </td>
                      <td className="py-2 px-2 text-right" style={{ color: 'var(--text-primary)' }}>{rep.total_leads}</td>
                      <td className="py-2 px-2 text-right" style={{ color: 'var(--text-primary)' }}>{rep.recent_leads_30d}</td>
                      <td className="py-2 px-2 text-right text-emerald-400">{rep.recent_conversions_30d}</td>
                      <td className="py-2 px-2 text-right" style={{ color: 'var(--text-primary)' }}>{rep.total_customers}</td>
                      <td className="py-2 px-2 text-right font-semibold" style={{ color: rep.conversion_rate >= 30 ? '#10B981' : rep.conversion_rate >= 15 ? '#FBBF24' : '#EF4444' }}>
                        {rep.conversion_rate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards — 3-way split (Leads / Pipeline / Signed) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'var(--primary-muted)' }}>
                <Target size={20} style={{ color: 'var(--primary)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats?.open_leads || 0}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Open Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'rgba(245, 158, 11, 0.2)' }}>
                <Clock size={20} style={{ color: 'var(--warning)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats?.pipeline_customers || 0}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>In Pipeline</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                <Users size={20} style={{ color: 'var(--success)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats?.signed_customers || 0}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Signed Customers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'rgba(168, 85, 247, 0.2)' }}>
                <TrendingUp size={20} style={{ color: '#a855f7' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats?.conversion_rate || 0}%</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Conversion</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'leads' ? 'default' : 'outline'}
          onClick={() => setActiveTab('leads')}
          className="gap-2"
          data-testid="tab-leads"
        >
          <Target size={16} />
          My Leads ({leads.length})
        </Button>
        <Button
          variant={activeTab === 'customers' ? 'default' : 'outline'}
          onClick={() => setActiveTab('customers')}
          className="gap-2"
          data-testid="tab-customers"
        >
          <Building2 size={16} />
          My Leads & Customers ({customers.length})
        </Button>
      </div>

      {/* Leads List */}
      {activeTab === 'leads' && (
        <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: 'var(--text-primary)' }}>Assigned Leads</CardTitle>
          </CardHeader>
          <CardContent>
            {leads.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                <Target size={48} className="mx-auto mb-3 opacity-30" />
                <p>No leads assigned to you yet</p>
                <p className="text-sm mt-1">Send intake invites to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leads.map((lead) => {
                  const statusStyle = STATUS_COLORS[lead.status] || STATUS_COLORS.DRAFT;
                  return (
                    <div
                      key={lead.id}
                      className="flex items-center justify-between p-4 rounded-lg"
                      style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
                      data-testid={`lead-${lead.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {lead.company_name || 'Unnamed Company'}
                          </p>
                          <Badge className={`${statusStyle.bg} ${statusStyle.text}`}>
                            {statusStyle.label}
                          </Badge>
                          {lead.lead_source && (
                            <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>
                              {lead.lead_source}
                            </span>
                          )}
                          {view === 'team' && lead.sales_rep_name && (
                            <span className="text-xs px-2 py-0.5 rounded flex items-center gap-1" style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#A855F7' }}>
                              <Users size={10} /> {lead.sales_rep_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
                          {lead.contact_email && (
                            <a href={`mailto:${lead.contact_email}`} className="flex items-center gap-1 hover:underline">
                              <Mail size={12} />
                              {lead.contact_email}
                            </a>
                          )}
                          {lead.form_data?.business_info?.phone && (
                            <a href={`tel:${lead.form_data.business_info.phone}`} className="flex items-center gap-1 hover:underline">
                              <Phone size={12} />
                              {lead.form_data.business_info.phone}
                            </a>
                          )}
                          {lead.num_trucks && (
                            <span>{lead.num_trucks} trucks</span>
                          )}
                          {lead.credit_requested && (
                            <span>${lead.credit_requested} credit</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(lead.status === 'PROSPECT' || lead.status === 'LINK_SENT') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(lead)}
                            data-testid={`edit-lead-${lead.id}`}
                            className="gap-1.5"
                            title="Edit lead info"
                          >
                            <Pencil size={14} />
                          </Button>
                        )}
                        {(lead.status === 'PROSPECT' || lead.status === 'LINK_SENT') && (() => {
                          const leadEmail = lead.form_data?.business_info?.email || lead.email || lead.contact_email || '';
                          const hasEmail = !!leadEmail && leadEmail.includes('@');
                          return (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSendLink(lead.id, lead.company_name || 'this lead', true)}
                                data-testid={`email-link-${lead.id}`}
                                className="gap-1.5"
                                title={hasEmail ? `Email link to ${leadEmail}` : 'No email on file — use Copy Link instead'}
                                disabled={!hasEmail}
                              >
                                <Mail size={14} />
                                {lead.status === 'LINK_SENT' ? 'Resend Email' : 'Email Link'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSendLink(lead.id, lead.company_name || 'this lead', false)}
                                data-testid={`copy-link-${lead.id}`}
                                className="gap-1.5"
                                title="Copy link to clipboard (for LinkedIn / SMS / phone)"
                              >
                                <LinkIcon size={14} />
                                Copy Link
                              </Button>
                            </>
                          );
                        })()}
                        {/* Manager-only reassign dropdown */}
                        {isManager && view === 'team' && salesReps.length > 0 && (
                          <select
                            defaultValue=""
                            onChange={(e) => handleReassign(lead.id, e.target.value)}
                            className="h-8 px-2 rounded-md text-xs"
                            style={{ background: 'var(--surface)', borderColor: 'var(--border)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                            data-testid={`reassign-${lead.id}`}
                            title="Reassign to another rep"
                          >
                            <option value="">Reassign…</option>
                            {salesReps.filter(r => r.id !== lead.sales_rep_id).map(r => (
                              <option key={r.id} value={r.id}>{r.full_name || r.email}</option>
                            ))}
                          </select>
                        )}
                        <div className="text-right text-sm" style={{ color: 'var(--text-muted)' }}>
                          {new Date(lead.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Customers List */}
      {activeTab === 'customers' && (
        <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: 'var(--text-primary)' }}>My Leads & Customers</CardTitle>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              Every record you own. Stage badge shows where each one sits in the funnel.
            </p>
          </CardHeader>
          <CardContent>
            {customers.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                <Users size={48} className="mx-auto mb-3 opacity-30" />
                <p>No leads or customers assigned to you yet</p>
                <p className="text-sm mt-1">Approved leads become customers</p>
              </div>
            ) : (
              <div className="space-y-3">
                {customers.map((customer) => {
                  const state = customer.onboarding_state || 'NEW_LEAD';
                  const SIGNED   = ['CONTRACT_SIGNED', 'ONBOARDED'];
                  const PIPELINE = ['APPROVED', 'ACCOUNTING_APPROVED', 'CONTRACT_SENT'];
                  const bucket = SIGNED.includes(state) ? 'signed' : PIPELINE.includes(state) ? 'pipeline' : 'lead';
                  const bucketMeta = {
                    signed:   { label: 'Signed',   color: 'var(--success)', bg: 'rgba(34, 197, 94, 0.15)',  border: 'rgba(34, 197, 94, 0.35)' },
                    pipeline: { label: 'Pipeline', color: 'var(--warning)', bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.35)' },
                    lead:     { label: 'Lead',     color: 'var(--primary)', bg: 'var(--primary-muted)',     border: 'var(--primary)' },
                  }[bucket];
                  return (
                  <div
                    key={customer.id}
                    onClick={() => navigate(`/ops/customers/${customer.id}`)}
                    className="flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all hover:translate-x-0.5"
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = bucketMeta.border; e.currentTarget.style.background = 'var(--surface)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg)'; }}
                    title="Open customer detail"
                    data-testid={`customer-${customer.id}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {customer.company_name}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-sm flex-wrap" style={{ color: 'var(--text-muted)' }}>
                        {customer.contact_email && (
                          <a
                            href={`mailto:${customer.contact_email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 hover:underline"
                            title={`Email ${customer.contact_email}`}
                          >
                            <Mail size={12} />
                            {customer.contact_email}
                          </a>
                        )}
                        {customer.fleet_size && (
                          <span>{customer.fleet_size} trucks</span>
                        )}
                        <Badge className="bg-blue-500/20 text-blue-400">
                          {customer.tenant_group || 'MM'}
                        </Badge>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border font-['JetBrains_Mono']"
                          style={{ color: bucketMeta.color, background: bucketMeta.bg, borderColor: bucketMeta.border }}>
                          {state.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-md"
                        style={{ color: bucketMeta.color, background: bucketMeta.bg }}>
                        {bucketMeta.label}
                      </span>
                      <ChevronRight size={16} style={{ color: 'var(--text-faint)' }} />
                    </div>
                  </div>
                );})}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Add Lead Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="max-w-lg" data-testid="add-lead-modal">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Lead' : 'Add Lead to My Pipeline'}</DialogTitle>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {editingId
                ? 'Update fields before the prospect submits their application.'
                : 'Quick capture a prospect you found. You can send them a pre-filled intake link after.'}
            </p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Mountain Hauling LLC"
                data-testid="lead-company-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="Bob Smith"
                  data-testid="lead-contact-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="406-555-0142"
                  data-testid="lead-phone-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="bob@mountainhauling.com"
                data-testid="lead-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="num_trucks">Fleet Size</Label>
                <Input
                  id="num_trucks"
                  value={form.num_trucks}
                  onChange={(e) => setForm({ ...form, num_trucks: e.target.value })}
                  placeholder="25 trucks"
                />
              </div>
              <div>
                <Label htmlFor="credit_amount_requested">Credit Requested</Label>
                <Input
                  id="credit_amount_requested"
                  value={form.credit_amount_requested}
                  onChange={(e) => setForm({ ...form, credit_amount_requested: e.target.value })}
                  placeholder="$10,000"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="lead_source">Lead Source</Label>
              <select
                id="lead_source"
                value={form.lead_source}
                onChange={(e) => setForm({ ...form, lead_source: e.target.value })}
                className="w-full h-9 px-3 rounded-md border text-sm"
                style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--text-primary)' }}
                data-testid="lead-source-select"
              >
                <option value="">— Select —</option>
                <option value="Google Search">Google Search</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Referral">Referral</option>
                <option value="Trade Show">Trade Show</option>
                <option value="Cold Call">Cold Call</option>
                <option value="DOT Registry">DOT Registry</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label htmlFor="notes">{editingId ? 'Add a Note' : 'Notes'}</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={editingId ? 'New note (existing notes are preserved)' : 'Found on MT DOT registry, $5M annual revenue, owner is veteran'}
                rows={3}
                data-testid="lead-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={adding}>
              Cancel
            </Button>
            <Button onClick={handleAddLead} disabled={adding || !form.company_name.trim()} data-testid="submit-lead-btn">
              {adding ? 'Saving…' : (editingId ? 'Save Changes' : 'Add to Pipeline')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
